import { uiConfig } from '../../config/ui.config';
import { getPublisherId } from '../../pages/report/@data/store/store';
import {
  IColumnMemberDetails,
  IDataDelay,
  IDataDelayRequest,
  IMetadataElement,
  IPubCenterMetadataElement,
  IPubCenterReportTypeResponse,
} from '../../pages/report/DashboardCards/@data/store/schema';
import { ReportColumnMembersParams } from '../store/schema/models/ReportColumnMembersParams';
import { PubCenterReportingDataRequestBody, ReportingDataRequestBody } from '../store/schema/models/ReportingDataRequestBody';
import { PubCenterReportingDataResponse, ReportingDataResponse } from '../store/schema/models/ReportingDataResponse';
import { handleApiErrors, handleError } from '../utils/handleApiErrors';
import { buildRequestHeadersWithAuthToken } from '../utils/requestUtils';
import { ApiError } from './ApiService';

const tythonV5ReportKeyMap: Record<string, string> = {
  EstimatedRevenue: 'Revenue',
  SiteUrl: 'PropertyUrl',
  Date: 'DateTime',
  Currency: 'CurrencyCode',
};

const tythonV5ReportFilterKeyMap: Record<string, string> = {
  SiteUrl: 'SiteId',
};

const tythonV5BackwardKeyMapping: Record<string, string> = {
  PropertyUrl: 'SiteUrl',
  DateTime: 'Date',
  CurrencyCode: 'Currency',
  Revenue: 'EstimatedRevenue',
};

const reConstructedRequestBody = (requestBody) => {
  const remapColumns = (columns: string[]): string[] => {
    return columns.map((col) => tythonV5BackwardKeyMapping[col] || col);
  };

  const convertFilters = (filters: { Key: string; values: string[] }[]): Record<string, string[]> =>
    filters.reduce((acc, { Key, values }) => {
      acc[Key] = values;
      return acc;
    }, {} as Record<string, string[]>);

  return {
    ...requestBody,
    granularity: requestBody.grain,
    grain: undefined,
    callerGuid: undefined,
    requestOptions: { isUIClient: true },
    filters: convertFilters(requestBody.filters),
    columns: remapColumns(requestBody.columns),
  };
};

const remapDataKeys = (data) => {
  return data.map((entry) => {
    const remapped = {};
    Object.entries(entry).forEach(([key, value]) => {
      const newKey = tythonV5ReportKeyMap[key] || key;
      remapped[newKey] = value;
    });
    return remapped;
  });
};

const remapMetricKeys = (metrics) =>
  metrics.map(({ key, filterKey, ...rest }) => ({
    key: tythonV5ReportKeyMap[key] || key,
    filterKey: tythonV5ReportFilterKeyMap[key] || filterKey,
    ...rest,
  }));

export async function getReportData(
  requestBody: ReportingDataRequestBody,
  abortController: AbortController
): Promise<{ data: ReportingDataResponse; aborted: boolean }> {
  const baseUrl = `${uiConfig.getV5ReportingApiBaseAddress()}`;
  const headers = await buildRequestHeadersWithAuthToken(baseUrl);

  let response: Response;
  try {
    response = await fetch(baseUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reConstructedRequestBody(requestBody)),
      signal: abortController.signal,
    });
    if (!response.ok) {
      return handleApiErrors<{ data: ReportingDataResponse; aborted: boolean }>(response, baseUrl);
    }

    const responseJson = await response.json();
    if (responseJson) {
      const trimmedRes = remapDataKeys(responseJson.Data);
      return { data: { ...responseJson, Data: trimmedRes }, aborted: false };
    }
    return Promise.resolve({ aborted: false } as { data: ReportingDataResponse; aborted: boolean });
  } catch (err) {
    if (err.name === 'AbortError') {
      return Promise.resolve({ aborted: true } as { data: ReportingDataResponse; aborted: boolean });
    }
    const error = `getReportData operation failed for ${baseUrl}. Errors: ${err}`;
    return handleError(new ApiError(500, error), baseUrl);
  }
}

export async function getPubCenterReportData(
  requestBody: PubCenterReportingDataRequestBody,
  abortController: AbortController
): Promise<{ data: PubCenterReportingDataResponse; aborted: boolean }> {
  const url = `${uiConfig.getPubCenterReportingApiAddress('5')}`;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody),
      signal: abortController.signal,
    });
    if (!response.ok) {
      return handleApiErrors<{ data: PubCenterReportingDataResponse; aborted: boolean }>(response, url);
    }

    const responseJson = await response.json();
    if (responseJson) {
      return { data: responseJson, aborted: false };
    }
    return Promise.resolve({ aborted: false } as { data: PubCenterReportingDataResponse; aborted: boolean });
  } catch (err) {
    if (err.name === 'AbortError') {
      return Promise.resolve({ aborted: true } as { data: PubCenterReportingDataResponse; aborted: boolean });
    }
    const error = `getReportData operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getDimensions(reportType: string): Promise<IMetadataElement[]> {
  const baseUrl = `${uiConfig.getReportingApiBaseAddress()}`;
  const redirectUrl = `metadata/dimensions?reportType=${reportType}`;
  const url = baseUrl + redirectUrl;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IMetadataElement[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return remapMetricKeys(responseJson);
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `getDimensions operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getMetrics(reportType: string): Promise<IMetadataElement[]> {
  const baseUrl = `${uiConfig.getReportingApiBaseAddress()}`;
  const redirectUrl = `metadata/metrics?reportType=${reportType}`;
  const url = baseUrl + redirectUrl;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IMetadataElement[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      const tythonV5Response = remapMetricKeys(responseJson);
      return tythonV5Response;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `getMetrics operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getGranularity(reportType: string): Promise<IMetadataElement[]> {
  const baseUrl = `${uiConfig.getReportingApiBaseAddress()}`;
  const redirectUrl = `metadata/grain?reportType=${reportType}`;
  const url = baseUrl + redirectUrl;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IMetadataElement[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return responseJson;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `getGranularity operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getPubCenterMetaDataItem(reportType: string, metaDataType: string): Promise<IMetadataElement[]> {
  const url = `${uiConfig.getPubCenterReportingMetaDataApiAddress(reportType, metaDataType)}`;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IMetadataElement[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return responseJson.map((item: { Id: string; Name: string; AdditionalFields: object }) => ({
        key: item.Id,
        filterKey: item.Id,
        displayName: item.Name,
        incompatibleFields: [],
        AdditionalFields: item.AdditionalFields,
      }));
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `get ${metaDataType} operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getPubCenterMetaDataReportTypes(item: string): Promise<IPubCenterReportTypeResponse[]> {
  const url = `${uiConfig.getPubCenterReportingMetaDataItemApiAddress(getPublisherId(), item)}`;

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IPubCenterReportTypeResponse[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return responseJson;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `get ${item} operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getPubCenterMetadataDimensions(
  publisherId: string,
  dimensionType: string,
  hierarchy?: string
): Promise<IPubCenterMetadataElement[]> {
  const url = hierarchy
    ? uiConfig.getPubCenterReportingMetadataDimensionsApiAddress(publisherId, dimensionType, hierarchy)
    : uiConfig.getPubCenterReportingMetadataDimensionsApiAddress(publisherId, dimensionType);
  const headers = await buildRequestHeadersWithAuthToken(url);
  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IPubCenterMetadataElement[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return responseJson;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `get ${dimensionType} operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getColumnMembers(requestParams: ReportColumnMembersParams): Promise<IColumnMemberDetails[]> {
  const tythonV5RequestParams = requestParams;
  if (tythonV5ReportFilterKeyMap[tythonV5RequestParams.column]) {
    tythonV5RequestParams.column = tythonV5ReportFilterKeyMap[tythonV5RequestParams.column];
  }

  const baseUrl = `${uiConfig.getReportingApiBaseAddress()}`;
  const redirectUrl = `metadata/columnMembers?${new URLSearchParams(tythonV5RequestParams)}`;
  const url = baseUrl + encodeURIComponent(redirectUrl);

  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return handleApiErrors<IColumnMemberDetails[]>(response, url);
    }

    const responseJson = await response.json();

    if (responseJson) {
      return responseJson;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `getColumnMembers operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}

export async function getDataDelay(requestBody: IDataDelayRequest): Promise<IDataDelay[]> {
  const url = `${uiConfig.getDataDelayApiAddress()}`;
  const headers = await buildRequestHeadersWithAuthToken(url);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      return handleApiErrors<IDataDelay[]>(response, url);
    }

    const responseJson = await response.json();
    if (responseJson) {
      return responseJson;
    }
    return Promise.resolve([]);
  } catch (err) {
    const error = `getDataDelay operation failed for ${url}. Errors: ${err}`;
    return handleError(new ApiError(500, error), url);
  }
}
